import { createRootRoute, createRoute, Navigate, Outlet } from "@tanstack/react-router";
import { SignInPage } from "@/pages/auth/sign-in";
import { SignUpPage } from "@/pages/auth/sign-up";
import { AuthCallbackPage } from "@/pages/auth/callback";
import { SelectTenantPage } from "@/pages/select-tenant";
import { TenantDashboardPage } from "@/pages/tenant/dashboard";
import { FundsListPage } from "@/pages/tenant/funds-list";
import { FundsNewPage } from "@/pages/tenant/funds-new";
import { FundsEditPage } from "@/pages/tenant/funds-edit";
import { LpsListPage } from "@/pages/tenant/lps-list";
import { LpsNewPage } from "@/pages/tenant/lps-new";
import { LpsEditPage } from "@/pages/tenant/lps-edit";
import { DocumentsListPage } from "@/pages/tenant/documents-list";
import { DocumentsUploadPage } from "@/pages/tenant/documents-upload";
import { DocumentReviewPage } from "@/pages/tenant/document-review";
import { ReviewQueuePage } from "@/pages/tenant/review-queue";
import { MfnListPage } from "@/pages/tenant/mfn-list";
import { MfnDetailPage } from "@/pages/tenant/mfn-detail";
import { SimulatorPage } from "@/pages/tenant/simulator";
import { MatrixPage } from "@/pages/tenant/matrix";
import { ObligationsPage } from "@/pages/tenant/obligations";
import { CalendarPage } from "@/pages/tenant/calendar";
import { RequirementsPage } from "@/pages/tenant/requirements";
import { ComplianceTaskPage } from "@/pages/tenant/compliance-task";
import { EthicsPage } from "@/pages/tenant/ethics";
import { ContributionsPage } from "@/pages/tenant/contributions";
import { RestrictedListPage } from "@/pages/tenant/restricted-list";
import { FilingsListPage } from "@/pages/tenant/filings-list";
import { FilingWorkspacePage } from "@/pages/tenant/filing-workspace";
import { FilingPackagePage } from "@/pages/tenant/filing-package";
import { ManualPage } from "@/pages/tenant/manual";
import { ManualSectionPage } from "@/pages/tenant/manual-section";
import { ManualPackagePage } from "@/pages/tenant/manual-package";
import { AuditPage } from "@/pages/tenant/audit";
import { AuditPackPage } from "@/pages/tenant/audit-pack";
import { LandingPage } from "@/pages/landing";
import { ProtectedRoute } from "@/components/auth/protected-route";
import { useAuth } from "@/lib/auth/auth-provider";

export const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <main className="bg-neutral-975 flex min-h-dvh items-center justify-center text-sm text-neutral-400">
        Loading…
      </main>
    );
  }
  // Logged-in users go to their workspace; everyone else sees the marketing site.
  return user ? <Navigate to="/select-tenant" replace /> : <LandingPage />;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomeRedirect,
});

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  component: SignInPage,
});

const signUpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-up",
  component: SignUpPage,
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
  component: AuthCallbackPage,
});

const selectTenantRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/select-tenant",
  component: () => (
    <ProtectedRoute>
      <SelectTenantPage />
    </ProtectedRoute>
  ),
});

// Tenant-scoped routes. Auth is enforced by ProtectedRoute; membership in the
// specific tenant is enforced by TenantLayout (which queries via RLS — non-members
// see "tenant not found").

const tenantDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/dashboard",
  component: () => (
    <ProtectedRoute>
      <TenantDashboardPage />
    </ProtectedRoute>
  ),
});

const fundsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/funds",
  component: () => (
    <ProtectedRoute>
      <FundsListPage />
    </ProtectedRoute>
  ),
});

const fundsNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/funds/new",
  component: () => (
    <ProtectedRoute>
      <FundsNewPage />
    </ProtectedRoute>
  ),
});

const fundsEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/funds/$fundId/edit",
  component: () => (
    <ProtectedRoute>
      <FundsEditPage />
    </ProtectedRoute>
  ),
});

const lpsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/lps",
  component: () => (
    <ProtectedRoute>
      <LpsListPage />
    </ProtectedRoute>
  ),
});

const lpsNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/lps/new",
  component: () => (
    <ProtectedRoute>
      <LpsNewPage />
    </ProtectedRoute>
  ),
});

const lpsEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/lps/$lpId/edit",
  component: () => (
    <ProtectedRoute>
      <LpsEditPage />
    </ProtectedRoute>
  ),
});

const documentsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/documents",
  component: () => (
    <ProtectedRoute>
      <DocumentsListPage />
    </ProtectedRoute>
  ),
});

const documentsUploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/documents/upload",
  component: () => (
    <ProtectedRoute>
      <DocumentsUploadPage />
    </ProtectedRoute>
  ),
});

const documentReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/documents/$documentId",
  component: () => (
    <ProtectedRoute>
      <DocumentReviewPage />
    </ProtectedRoute>
  ),
});

const reviewQueueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/review-queue",
  component: () => (
    <ProtectedRoute>
      <ReviewQueuePage />
    </ProtectedRoute>
  ),
});

const mfnListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/mfn",
  component: () => (
    <ProtectedRoute>
      <MfnListPage />
    </ProtectedRoute>
  ),
});

const mfnDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/mfn/$triggerId",
  component: () => (
    <ProtectedRoute>
      <MfnDetailPage />
    </ProtectedRoute>
  ),
});

const simulatorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/simulator",
  component: () => (
    <ProtectedRoute>
      <SimulatorPage />
    </ProtectedRoute>
  ),
});

const matrixRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/matrix",
  component: () => (
    <ProtectedRoute>
      <MatrixPage />
    </ProtectedRoute>
  ),
});

const obligationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/obligations",
  component: () => (
    <ProtectedRoute>
      <ObligationsPage />
    </ProtectedRoute>
  ),
});

const calendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/calendar",
  component: () => (
    <ProtectedRoute>
      <CalendarPage />
    </ProtectedRoute>
  ),
});

const requirementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/requirements",
  component: () => (
    <ProtectedRoute>
      <RequirementsPage />
    </ProtectedRoute>
  ),
});

const complianceTaskRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/compliance/$taskId",
  component: () => (
    <ProtectedRoute>
      <ComplianceTaskPage />
    </ProtectedRoute>
  ),
});

const filingsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/filings",
  component: () => (
    <ProtectedRoute>
      <FilingsListPage />
    </ProtectedRoute>
  ),
});

const filingWorkspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/filings/$filingId",
  component: () => (
    <ProtectedRoute>
      <FilingWorkspacePage />
    </ProtectedRoute>
  ),
});

const filingPackageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/filings/$filingId/package",
  component: () => (
    <ProtectedRoute>
      <FilingPackagePage />
    </ProtectedRoute>
  ),
});

// Compliance manual. The static `/manual/package` route is registered before the
// dynamic `/manual/$sectionKey` so "package" is never read as a section key.
const manualRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/manual",
  component: () => (
    <ProtectedRoute>
      <ManualPage />
    </ProtectedRoute>
  ),
});

const manualPackageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/manual/package",
  component: () => (
    <ProtectedRoute>
      <ManualPackagePage />
    </ProtectedRoute>
  ),
});

const manualSectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/manual/$sectionKey",
  component: () => (
    <ProtectedRoute>
      <ManualSectionPage />
    </ProtectedRoute>
  ),
});

// Form ADV Part 2A brochure — same parameterized pages, doc_type discriminator
// (ADR-0007). Static `/brochure/package` before the dynamic `/brochure/$sectionKey`.
const brochureRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/brochure",
  component: () => (
    <ProtectedRoute>
      <ManualPage docType="adv_2a_brochure" />
    </ProtectedRoute>
  ),
});

const brochurePackageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/brochure/package",
  component: () => (
    <ProtectedRoute>
      <ManualPackagePage docType="adv_2a_brochure" />
    </ProtectedRoute>
  ),
});

const brochureSectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/brochure/$sectionKey",
  component: () => (
    <ProtectedRoute>
      <ManualSectionPage docType="adv_2a_brochure" />
    </ProtectedRoute>
  ),
});

const ethicsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/ethics",
  component: () => (
    <ProtectedRoute>
      <EthicsPage />
    </ProtectedRoute>
  ),
});

const contributionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/contributions",
  component: () => (
    <ProtectedRoute>
      <ContributionsPage />
    </ProtectedRoute>
  ),
});

const restrictedListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/restricted-list",
  component: () => (
    <ProtectedRoute>
      <RestrictedListPage />
    </ProtectedRoute>
  ),
});

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/audit",
  component: () => (
    <ProtectedRoute>
      <AuditPage />
    </ProtectedRoute>
  ),
});

const auditPackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$slug/audit-pack/$fundId",
  component: () => (
    <ProtectedRoute>
      <AuditPackPage />
    </ProtectedRoute>
  ),
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  signInRoute,
  signUpRoute,
  authCallbackRoute,
  selectTenantRoute,
  tenantDashboardRoute,
  fundsListRoute,
  fundsNewRoute,
  fundsEditRoute,
  lpsListRoute,
  lpsNewRoute,
  lpsEditRoute,
  documentsListRoute,
  documentsUploadRoute,
  documentReviewRoute,
  reviewQueueRoute,
  mfnListRoute,
  mfnDetailRoute,
  simulatorRoute,
  matrixRoute,
  obligationsRoute,
  calendarRoute,
  requirementsRoute,
  complianceTaskRoute,
  filingsListRoute,
  filingWorkspaceRoute,
  filingPackageRoute,
  manualRoute,
  manualPackageRoute,
  manualSectionRoute,
  brochureRoute,
  brochurePackageRoute,
  brochureSectionRoute,
  ethicsRoute,
  contributionsRoute,
  restrictedListRoute,
  auditRoute,
  auditPackRoute,
]);
